/**
 * GmailConnectSection – shown inside InboxFormModal when protocol === 'google_oauth'.
 *
 * NEW inbox (no initialData):
 *   1. User selects description/label
 *   2. Clicks "חבר חשבון Gmail" → redirects to Google OAuth
 *   On return the page handles ?oauth=success&inbox_id=<id>
 *
 * EXISTING inbox (has initialData):
 *   -cted: shows email + "חבר מחדש" + GmailLabelsPicker
 *   - If not connected: "חבר חשבון Gmail" button
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { GmailLabelsPicker } from './GmailLabelsPicker';
import { EmailInboxConfig, InboxLabel } from '@/types/EmailInbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useLazyGetGmailConnectUrlQuery, useLazyGetGmailReconnectUrlQuery } from '@/store/emailInboxApi';
import { toast } from 'sonner';
import { CheckCircle, Mail, RefreshCw } from 'lucide-react';

interface Props {
  initialData: EmailInboxConfig | null;
  /** Only for new inbox — the label the user selected */
  selectedLabel: InboxLabel;
  onLabelChange: (v: InboxLabel) => void;
}

export function GmailConnectSection({ initialData, selectedLabel, onLabelChange }: Props) {
  const isNew = !initialData;
  const isConnected = !!initialData?.is_gmail_connected;

  const [triggerConnect, { isLoading: isConnecting }] = useLazyGetGmailConnectUrlQuery();
  const [triggerReconnect, { isLoading: isReconnecting }] = useLazyGetGmailReconnectUrlQuery();

  async function handleConnectNew() {
    try {
      const result = await triggerConnect(selectedLabel).unwrap();
      window.location.href = result.auth_url;
    } catch {
      toast.error('שגיאה בפתיחת חיבור Google');
    }
  }

  async function handleReconnect() {
    if (!initialData) return;
    try {
      const result = await triggerReconnect(initialData.id).unwrap();
      window.location.href = result.auth_url;
    } catch {
      toast.error('שגיאה בפתיחת חיבור Google');
    }
  }

  /* ─── New inbox: just label picker + connect button ─────────────────────── */
  if (isNew) {
    return (
      <div className="space-y-4 border rounded-lg p-4 bg-gray-50">
        <p className="text-sm font-medium text-gray-700">
          חיבור Gmail חדש
        </p>

        <div className="space-y-1">
          <Label>תיאור התיבה</Label>
          <Select value={selectedLabel} onValueChange={(v) => onLabelChange(v as InboxLabel)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="support">שירות לקוחות</SelectItem>
              <SelectItem value="sales">מכירות</SelectItem>
              <SelectItem value="billing">גביה</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-700">
          <p>לאחר לחיצה על הכפתור תועבר לדף אישור Google.</p>
          <p>לאחר האישור תחזור אוטומטית לכאן עם הגדרת Labels.</p>
        </div>

        <Button
          type="button"
          onClick={handleConnectNew}
          disabled={isConnecting}
          className="w-full gap-2"
        >
          {isConnecting ? <Spinner className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
          חבר חשבון Gmail
        </Button>
      </div>
    );
  }

  /* ─── Existing inbox ─────────────────────────────────────────────────────── */
  return (
    <div className="space-y-4">
      {/* Connection status */}
      <div className={`flex items-center justify-between rounded-lg border p-3 ${
        isConnected ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
      }`}>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          ) : (
            <Mail className="h-5 w-5 text-amber-600 flex-shrink-0" />
          )}
          <div className="text-sm">
            {isConnected ? (
              <>
                <span className="font-medium text-green-800">Gmail מחובר</span>
                <span className="text-green-700 mr-2">{initialData.email}</span>
                {initialData.google_token_expiry && (
                  <span className="text-xs text-green-600 block">
                    תפוגה: {new Date(initialData.google_token_expiry).toLocaleString('he-IL')}
                  </span>
                )}
              </>
            ) : (
              <span className="font-medium text-amber-800">Gmail לא מחובר</span>
            )}
          </div>
        </div>

        <Button
          type="button"
          size="sm"
          variant={isConnected ? 'outline' : 'default'}
          onClick={handleReconnect}
          disabled={isReconnecting}
          className="gap-1.5 flex-shrink-0"
        >
          {isReconnecting
            ? <Spinner className="h-3 w-3" />
            : <RefreshCw className="h-3.5 w-3.5" />
          }
          {isConnected ? 'חבר מחדש' : 'חבר חשבון Gmail'}
        </Button>
      </div>

      {/* Labels picker – only when connected */}
      {isConnected && (
        <GmailLabelsPicker inboxId={initialData.id} />
      )}
    </div>
  );
}
